import React, { Suspense, useState, useEffect } from 'react';
import CategoryRail from './CategoryRail';
import FeaturedHeroCarousel from './FeaturedHeroCarousel';
import Header from './common/Header';
import Footer from './common/Footer';
import LoadingSpinner from './common/LoadingSpinner';
import ErrorBoundary from './common/ErrorBoundary';
import LoginCTASection from './PlaceholderRails/LoginCTASection';
import spotlightService from '../services/SpotlightService';
import { cacheService } from '../services/CacheService';
import { useNavigate, useLocation } from 'react-router-dom';
import PDPModal from './PDPModal';
import { useContinueWatching } from '../hooks/useContinueWatching';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { userDataService } from '../services/UserDataService';
import './Homepage.css';
import { toRailCategory } from '../utils/categoryStyle';

const SeriesPage = ({ onCategorySelect, onMovieSelect, onSeriesSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [featuredCategory, setFeaturedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Continue Watching and Auth hooks
  const {
    continueWatchingData,
    hasContinueWatching,
    loading: continueWatchingLoading,
  } = useContinueWatching();
  const { isAuthenticated } = useStreamAuth();

  // Resumption data for ThumbnailHoverModal
  const [resumptionData, setResumptionData] = useState({
    continueWatching: [],
    watchAgain: []
  });

  // PDP Modal state for channel query parameter handling
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showPDPModal, setShowPDPModal] = useState(false);
  const [hasProcessedPermalink, setHasProcessedPermalink] = useState(false);

  // Fetch resumption data for ThumbnailHoverModal
  useEffect(() => {
    const fetchResumptionData = async () => {
      if (!isAuthenticated) {
        setResumptionData({ continueWatching: [], watchAgain: [] });
        return;
      }

      try {
        const data = await userDataService.refreshResumptionsData();
        setResumptionData({
          continueWatching: data?.['continue-watching'] || [],
          watchAgain: data?.['watch-again'] || []
        });
      } catch (error) {
        console.error('SeriesPage: Error fetching resumption data:', error);
        setResumptionData({ continueWatching: [], watchAgain: [] });
      }
    };

    fetchResumptionData();
  }, [isAuthenticated]);

  const fetchSeriesData = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🎬 SeriesPage: Fetching series data...');
      console.log('🔍 Base URL:', spotlightService.baseURL);
      console.log('🔍 Full URL:', spotlightService.baseURL + 'series/US/website?include_child_info=true');
      
      const data = await spotlightService.getSeriesHomePage();
      console.log('✅ SeriesPage: Data received:', data);

      // Process homepage data to extract categories with their channels
      const allCategories =
        data.series?.map(toRailCategory) || [];

      // Use the first category as the featured carousel
      if (allCategories.length > 0) {
        setFeaturedCategory(allCategories[0]);
        // Regular categories are everything except the first one
        setCategories(allCategories.slice(1));
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error('Error fetching series data:', err);
      
      // Check if this is a 502 error (backend gateway issue)
      const is502Error = err.message.includes('502');
      const maxRetries = 2;
      
      if (is502Error && retryCount < maxRetries) {
        console.log(`SeriesPage: Retrying fetch (attempt ${retryCount + 1}/${maxRetries})...`);
        // Wait a bit before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return fetchSeriesData(retryCount + 1);
      }
      
      // Provide a more specific error message for 502 errors
      if (is502Error) {
        setError(
          'The series service is temporarily unavailable (502 Bad Gateway). This is a backend server issue. Please try again in a few moments or contact support if the problem persists.'
        );
      } else {
        setError(`Failed to load series: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if we have preloaded data in cache first
    const checkPreloadedData = async () => {
      try {
        // Check if we have cached data from header preloading
        if (cacheService.hasSeriesPage()) {
          // If we have cached data, try to fetch it immediately
          const data = await spotlightService.getSeriesHomePage();

          // Process homepage data to extract categories with their channels
          const allCategories =
            data.series?.map(toRailCategory) || [];

          // Use the first category as the featured carousel
          if (allCategories.length > 0) {
            setFeaturedCategory(allCategories[0]);
            setCategories(allCategories.slice(1));
          } else {
            setCategories([]);
          }
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn(
          'SeriesPage: Failed to use preloaded data, falling back to normal fetch:',
          err,
        );
      }

      // If no preloaded data or it failed, fetch normally
      fetchSeriesData();
    };

    checkPreloadedData();
  }, []);

  // Featured category is now set from the first category in the series data
  // No separate fetch needed - see fetchSeriesData and checkPreloadedData

  // Handle channel query parameter for PDP modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const channelSlug = searchParams.get('channel');

    if (channelSlug && categories.length > 0 && !hasProcessedPermalink) {
      // Find the channel across all categories
      let foundChannel = null;
      for (const category of categories) {
        if (category.channels && Array.isArray(category.channels)) {
          foundChannel = category.channels.find(
            channel =>
              channel.slug === channelSlug ||
              channel._id === channelSlug ||
              channel.permalink === channelSlug,
          );
          if (foundChannel) break;
        }
      }

      if (foundChannel) {
        setSelectedChannel(foundChannel);
        setShowPDPModal(true);
        setHasProcessedPermalink(true);
      } else {
        setHasProcessedPermalink(true);
      }
    }
  }, [location.search, categories, hasProcessedPermalink]);

  // Handle PDP modal close
  const handlePDPModalClose = () => {
    setShowPDPModal(false);
    setSelectedChannel(null);

    // Clear the URL parameter to prevent reopening
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('channel')) {
      searchParams.delete('channel');
      const newUrl = `${window.location.pathname}${
        searchParams.toString() ? '?' + searchParams.toString() : ''
      }`;
      window.history.replaceState({}, '', newUrl);
    }
  };

  if (error) {
    const is502Error = error.includes('502');
    
    return (
      <div className="min-h-screen series-page bg-page">
        <Header />
        <main className="container px-6 py-8 mx-auto">
          <div className={`p-6 text-center border rounded-lg ${
            is502Error 
              ? 'border-yellow-500 bg-yellow-900/20' 
              : 'border-red-500 bg-red-900/30'
          }`}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`w-12 h-12 mx-auto mb-4 ${
                is502Error ? 'text-yellow-500' : 'text-red-500'
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {is502Error ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              )}
            </svg>
            <h2 className="mb-2 text-xl font-bold text-white">
              {is502Error ? 'Servicio No Disponible Temporalmente' : 'Error al Cargar Series'}
            </h2>
            <p className="max-w-2xl mx-auto mb-4 text-white/70">{error}</p>
            {is502Error && (
              <div className="max-w-2xl p-4 mx-auto mb-4 text-sm text-left rounded-lg bg-white/5">
                <p className="mb-2 font-semibold text-white">Detalles Técnicos:</p>
                <ul className="space-y-1 text-white/60">
                  <li>• <strong>Código de Error:</strong> 502 Bad Gateway</li>
                  <li>• <strong>Endpoint:</strong> /api/series/US/website</li>
                  <li>• <strong>Causa:</strong> Problema del servidor API</li>
                  <li>• <strong>Nota:</strong> La página de películas funciona normalmente</li>
                </ul>
              </div>
            )}
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 font-medium text-white rounded-full bg-accent hover:bg-accent/80"
              >
                Ir al Inicio
              </button>
              <button
                onClick={() => navigate('/movies')}
                className="px-6 py-2 font-medium text-white rounded-full bg-blue-600 hover:bg-blue-700"
              >
                Ver Películas
              </button>
              <button
                onClick={() => fetchSeriesData()}
                className="px-6 py-2 font-medium text-white rounded-full bg-white/10 hover:bg-white/20"
              >
                Intentar de Nuevo
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen series-page bg-page homepage">
      <Header />

      {/* Full-screen Hero Carousel - Outside container for full width */}
      <ErrorBoundary fallback="Error al cargar contenido destacado">
        <Suspense fallback={<LoadingSpinner message="Cargando contenido destacado..." />}>
          {featuredCategory && (
            <FeaturedHeroCarousel
              category={featuredCategory}
              categorySlug="featured-spanish"
              language="es"
            />
          )}
        </Suspense>
      </ErrorBoundary>

      {/* Content Rails - With overlapping effect */}
      <main className="container relative mx-auto">
        <ErrorBoundary fallback="Error al cargar categorías">
          <Suspense fallback={<LoadingSpinner message="Cargando categorías..." />}>
            {loading ? (
              <LoadingSpinner message="Cargando series..." />
            ) : categories.length > 0 ? (
              categories.map((category, idx) => {
                // Insert Continue Watching rail as the third rail (after index 1) for authenticated users
                // OR insert CTA sections for non-authenticated users
                if (idx === 2) {
                  return (
                    <React.Fragment key={`continue-watching-${category._id}`}>
                      {/* Continue Watching Rail for Authenticated Users */}
                      {isAuthenticated && hasContinueWatching && (
                        <div className="mb-8">
                          <CategoryRail
                            category={{
                              _id: 'continue-watching',
                              name: 'Seguir Viendo',
                              display_name: 'Seguir Viendo',
                              channels: continueWatchingData,
                              style: 'continue-watching',
                            }}
                            preCalculatedContinueWatching={resumptionData.continueWatching}
                            preCalculatedWatchAgain={resumptionData.watchAgain}
                            onCategorySelect={onCategorySelect}
                            onMovieSelect={onMovieSelect}
                            onSeriesSelect={onSeriesSelect}
                          />
                        </div>
                      )}

                      {/* Continue Watching CTA for Non-Authenticated Users */}
                      {!isAuthenticated && (
                        <LoginCTASection
                          sectionTitle="Seguir Viendo"
                          title="Continúa donde lo dejaste."
                          subtitle="Inicia sesión para seguir viendo tus series favoritas."
                        />
                      )}

                      {/* Regular Category Rail */}
                      <div
                        className={idx === 0 ? '-mt-72 md:-mt-80 xl:-mt-80 z-20 relative' : 'mb-8'}
                      >
                        <CategoryRail
                          category={category}
                          preCalculatedContinueWatching={resumptionData.continueWatching}
                          preCalculatedWatchAgain={resumptionData.watchAgain}
                          onCategorySelect={onCategorySelect}
                          onMovieSelect={onMovieSelect}
                          onSeriesSelect={onSeriesSelect}
                        />
                      </div>
                    </React.Fragment>
                  );
                }

                // Insert Watch Again CTA for non-authenticated users at index 4
                if (idx === 4 && !isAuthenticated) {
                  return (
                    <React.Fragment key={`watch-again-${category._id}`}>
                      {/* Watch Again CTA for Non-Authenticated Users */}
                      <LoginCTASection
                        sectionTitle="Ver de Nuevo"
                        title="Redescubre tus favoritos."
                        subtitle="Inicia sesión para volver a ver tus series favoritas."
                      />

                      {/* Regular Category Rail */}
                      <div className="mb-8">
                        <CategoryRail
                          category={category}
                          preCalculatedContinueWatching={resumptionData.continueWatching}
                          preCalculatedWatchAgain={resumptionData.watchAgain}
                          onCategorySelect={onCategorySelect}
                          onMovieSelect={onMovieSelect}
                          onSeriesSelect={onSeriesSelect}
                        />
                      </div>
                    </React.Fragment>
                  );
                }

                // Regular category rail
                return (
                  <div
                    key={category._id}
                    className={idx === 0 ? '-mt-72 md:-mt-80 xl:-mt-80 z-20 relative' : 'mb-8'}
                  >
                    <CategoryRail
                      category={category}
                      preCalculatedContinueWatching={resumptionData.continueWatching}
                      preCalculatedWatchAgain={resumptionData.watchAgain}
                      onCategorySelect={onCategorySelect}
                      onMovieSelect={onMovieSelect}
                      onSeriesSelect={onSeriesSelect}
                    />
                  </div>
                );
              })
            ) : (
              <div className="py-12 text-center text-white/50">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-16 h-16 mx-auto mb-4 text-white/30"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                  />
                </svg>
                <h3 className="mb-2 text-lg font-medium text-white">No Hay Series Disponibles</h3>
                <p className="text-white/50">Vuelve más tarde para nuevo contenido.</p>
              </div>
            )}
          </Suspense>
        </ErrorBoundary>
      </main>

      <Footer />

      {/* PDP Modal for direct channel links */}
      {selectedChannel && (
        <PDPModal
          isVisible={showPDPModal}
          channel={selectedChannel}
          onClose={handlePDPModalClose}
        />
      )}
    </div>
  );
};

export default SeriesPage;
